import * as React from 'react';
import { connect } from 'react-redux';
import { compose, Dispatch, bindActionCreators } from 'redux';
import { State, StoryQuery } from '../redux/state';
import { Stories } from '../userApi';
import actions from '../redux/actions';
import { LProps, withLocalization } from '../utils/wrappers';
import './feedLiveRefresh.scss';

const POLL_MS = 35000;

interface Props {
  enabled: boolean;
  pendingCount: number;
  softPollCompetitionFeed: () => void;
  applyPendingFeedStories: () => void;
}

class FeedLiveRefresh extends React.Component<LProps<Props>> {
  private pollTimer: number | null = null;

  public componentDidMount() {
    this.syncPolling(this.props.enabled);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  public componentDidUpdate(prevProps: LProps<Props>) {
    if (prevProps.enabled !== this.props.enabled) {
      this.syncPolling(this.props.enabled);
    }
  }

  public componentWillUnmount() {
    this.clearPolling();
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private syncPolling(enabled: boolean) {
    this.clearPolling();
    if (!enabled) {
      return;
    }
    this.pollTimer = window.setInterval(this.poll, POLL_MS);
  }

  private clearPolling() {
    if (this.pollTimer !== null) {
      window.clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  private onVisibilityChange = () => {
    if (!this.props.enabled) {
      return;
    }
    if (!document.hidden) {
      this.poll();
    }
  }

  private poll = () => {
    if (!this.props.enabled) {
      return;
    }
    this.props.softPollCompetitionFeed();
  }

  private applyPending = () => {
    this.props.applyPendingFeedStories();
    if (typeof window !== 'undefined' && window.scrollY > 80) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public render() {
    const { pendingCount, t, enabled } = this.props;
    if (!enabled || pendingCount <= 0) {
      return null;
    }
    const label = pendingCount === 1
      ? t('1 new post')
      : t('{{count}} new posts', { count: pendingCount });
    return (
      <div className="cc__feed_live_refresh" role="status" aria-live="polite">
        <button
          type="button"
          className="cc__feed_live_refresh__chip"
          onClick={this.applyPending}
        >
          {label}
        </button>
      </div>
    );
  }
}

const isMainCompetitionFeed = (storyQuery: StoryQuery) =>
  !storyQuery.userID && !storyQuery.entityID && !storyQuery.competitionPledgeID && !storyQuery.storyID;

const mapStateToProps = (state: State): Partial<Props> => {
  const pending = (state.pendingFeedStories || []) as Stories;
  return {
    enabled: Boolean(state.token && state.competition && state.competition.id && isMainCompetitionFeed(state.storyQuery)),
    pendingCount: pending.length
  };
};

const mapDispatchToProps = (dispatch: Dispatch): Partial<Props> => bindActionCreators({
  softPollCompetitionFeed: actions.softPollCompetitionFeed,
  applyPendingFeedStories: actions.applyPendingFeedStories
}, dispatch);

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withLocalization('story')
)(FeedLiveRefresh) as React.ComponentClass;
